import React from "react";
import { motion } from "framer-motion";
import { Brain, Heart, CloudRain, Leaf, Users, GraduationCap } from "lucide-react";
import { servicesData } from "../../data/siteData";

const iconMap = {
  Brain: Brain,
  Heart: Heart,
  CloudRain: CloudRain,
  Leaf: Leaf,
  Users: Users,
  GraduationCap: GraduationCap
};

export const ServicesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 12
      }
    }
  };

  return (
    <section
      id="services"
      className="py-20 sm:py-32 bg-warm-bg relative z-20"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-warm-sage uppercase tracking-[0.2em] text-xs font-bold mb-4"
          >
            What I Offer
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-warm-brown mb-4"
          >
            How I Can Help You
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="font-sans text-warm-brown/70 max-w-2xl mx-auto"
          >
            Evidence-based therapies tailored to your unique journey
          </motion.p>
        </div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
        >
          {servicesData.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                whileHover={{ y: -8 }}
                className="service-card group bg-warm-card rounded-3xl p-8 border border-warm-rose/10 hover:shadow-xl hover:border-warm-rose/30 transition-all duration-300 cursor-pointer"
                data-testid={`service-card-${service.id}`}
              >
                {/* Icon */}
                <div className="mb-6">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="service-icon inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-warm-sage/20 text-warm-sage"
                  >
                    {IconComponent && <IconComponent size={28} />}
                  </motion.div>
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl text-warm-brown mb-3 group-hover:text-warm-rose transition-colors">
                  {service.title}
                </h3>
                <p className="font-sans text-warm-brown/70 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Hover indicator */}
                <div className="mt-6 flex items-center text-warm-rose opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-sm font-medium">Learn more</span>
                  <motion.span
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                    className="ml-2"
                  >
                    →
                  </motion.span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
